// src/app/(student)/exam/take/[attemptId]/page.tsx
"use client";

import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { SafeHtml } from "@/lib/utils/safe-html";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { useSession } from "next-auth/react";
import { toast } from "sonner";
import {
  Loader2,
  User,
  CheckCircle2,
  Maximize2,
  Minimize2,
  Delete,
  Trophy,
  Clock,
  ArrowLeft,
  AlertCircle,
  CheckCircle,
  XCircle,
  Circle,
  Lock,
  TrendingUp,
  Target,
  MinusCircle,
  Timer,
  LayoutGrid,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
} from "@/components/ui/sheet";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

interface Option {
  key: string;
  text: string;
  imageUrl: string | null;
  isCorrect?: boolean;
}

// AFTER:
interface MatchPairs {
  leftColumn: { header: string; items: string[] };
  rightColumn: { header: string; items: string[] };
}

interface Question {
  id: string;
  sequence: number;
  statement: string;
  imageUrl: string | null;
  topic: string;
  marks: number;
  negativeMarks: number;
  difficulty: "easy" | "medium" | "hard";
  options: Option[];
  type: "mcq" | "numerical" | "match"; // ✅ added 'match'
  correctAnswerExact?: number | null;
  correctAnswerMin?: number | null;
  correctAnswerMax?: number | null;
  matchPairs?: MatchPairs | null;
  explanation?: string | null;
}

interface ExamData {
  examId: string;
  examTitle: string;
  duration: number;
  totalQuestions: number;
  expiresAt: string;
  allowReview: boolean;
  questions: Question[];
  isPYQ?: boolean;
  pyqCategory?: { slug: string } | null;
}

// ✅ NEW: renders Column I / Column II table for match questions
const LEFT_LABELS = ["A", "B", "C", "D", "E", "F"];
const RIGHT_LABELS = ["i", "ii", "iii", "iv", "v", "vi"];

function MatchTable({ matchPairs }: { matchPairs: MatchPairs }) {
  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 mb-6">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="text-left px-4 py-2.5 font-medium w-1/2 text-gray-700">
              {matchPairs.leftColumn.header}
            </th>
            <th className="text-left px-4 py-2.5 font-medium w-1/2 text-gray-700">
              {matchPairs.rightColumn.header}
            </th>
          </tr>
        </thead>
        <tbody>
          {matchPairs.leftColumn.items.map((leftItem, index) => (
            <tr key={index} className="border-b border-gray-200 last:border-0">
              <td className="px-4 py-2.5 align-middle text-gray-700">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold mr-2 shrink-0">
                  {LEFT_LABELS[index]}
                </span>
                <SafeHtml html={leftItem} className="inline" />
              </td>
              <td className="px-4 py-2.5 align-middle text-gray-700">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold mr-2 shrink-0">
                  {RIGHT_LABELS[index]}
                </span>
                <SafeHtml
                  html={matchPairs.rightColumn.items[index]}
                  className="inline"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Numerical Calculator ───────────────────────────────────────────────────

function NumericalCalculator({
  questionId,
  value,
  onChange,
}: {
  questionId: string;
  value: string;
  onChange: (
    questionId: string,
    display: string,
    numValue: number | null,
  ) => void;
}) {
  const handleButton = (key: string) => {
    let next = value;
    if (key === "backspace") {
      next = value.slice(0, -1);
    } else if (key === "-") {
      next = value.startsWith("-") ? value.slice(1) : "-" + value;
    } else if (key === ".") {
      if (!value.includes(".")) next = value + ".";
    } else {
      next = value === "0" && key !== "." ? key : value + key;
    }
    const parsed = parseFloat(next);
    const isValid =
      next !== "" && next !== "-" && next !== "." && !isNaN(parsed);
    onChange(questionId, next, isValid ? parsed : null);
  };

  const buttons = [
    ["7", "8", "9"],
    ["4", "5", "6"],
    ["1", "2", "3"],
    [".", "0", "-"],
  ];

  return (
    <div className="flex flex-col items-start gap-4 py-4">
      <div className="w-72 bg-gray-900 text-white rounded-lg p-4 flex items-center justify-between min-h-[56px]">
        <span className="text-2xl font-mono tracking-widest">
          {value || "___"}
        </span>
        <button
          onClick={() => handleButton("backspace")}
          className="text-gray-400 hover:text-white transition-colors ml-2"
        >
          <Delete className="w-5 h-5" />
        </button>
      </div>
      <div className="grid grid-cols-3 gap-2 w-72">
        {buttons.map((row, i) =>
          row.map((key) => (
            <button
              key={`${i}-${key}`}
              onClick={() => handleButton(key)}
              className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-semibold text-lg rounded-lg py-4 transition-colors"
            >
              {key}
            </button>
          )),
        )}
      </div>
    </div>
  );
}

// ─── Guest Result Screen ───────────────────────────────────────────────────────

function GuestResultScreen({
  exam,
  answers,
  timeRemaining,
  timePerQuestion,
}: {
  exam: ExamData;
  answers: Record<string, string | number | null>;
  timeRemaining: number;
  timePerQuestion: Record<string, number>;
}) {
  const router = useRouter();
  const { status } = useSession();
  const isAuthenticated = status === "authenticated";
  const [activeTab, setActiveTab] = useState("summary");

  // Calculate score locally
  const result = useMemo(() => {
    let correct = 0;
    let incorrect = 0;
    let unattempted = 0;
    let score = 0;
    let correctTime = 0;
    let incorrectTime = 0;
    let unattemptedTime = 0;

    exam.questions.forEach((q) => {
      const ans = answers[q.id];
      const timeSpent = timePerQuestion[q.id] || 0;

      if (ans === undefined || ans === null || ans === "") {
        unattempted++;
        unattemptedTime += timeSpent;
        return;
      }

      let isCorrect = false;
      if (q.type === "mcq" || q.type === "match") {
        const correctOpt = q.options.find((o) => o.isCorrect);
        isCorrect = !!(correctOpt && ans === correctOpt.key);
      } else if (q.type === "numerical") {
        const numAns = parseFloat(ans as string);
        isCorrect =
          q.correctAnswerExact != null
            ? numAns === q.correctAnswerExact
            : q.correctAnswerMin != null && q.correctAnswerMax != null
              ? numAns >= (q.correctAnswerMin as number) && numAns <= (q.correctAnswerMax as number)
              : false;
      }

      if (isCorrect) {
        correct++;
        score += q.marks;
        correctTime += timeSpent;
      } else {
        incorrect++;
        score -= q.negativeMarks;
        incorrectTime += timeSpent;
      }
    });

    return { correct, incorrect, unattempted, score, correctTime, incorrectTime, unattemptedTime };
  }, [exam, answers, timePerQuestion]);

  const timeTaken = exam.duration * 60 - timeRemaining;
  const mins = Math.floor(timeTaken / 60);
  const secs = timeTaken % 60;
  
  const accuracy = result.correct + result.incorrect > 0 
    ? Math.round((result.correct / (result.correct + result.incorrect)) * 100)
    : 0;

  const totalTrackedTime = result.correctTime + result.incorrectTime + result.unattemptedTime;
  const correctTimePct = totalTrackedTime ? Math.round((result.correctTime / totalTrackedTime) * 100) : 0;
  const incorrectTimePct = totalTrackedTime ? Math.round((result.incorrectTime / totalTrackedTime) * 100) : 0;
  const unattemptedTimePct = totalTrackedTime ? Math.round((result.unattemptedTime / totalTrackedTime) * 100) : 0;

  const pieData = [
    { name: "Correct", value: result.correct, color: "#10b981" },
    { name: "Wrong", value: result.incorrect, color: "#ef4444" },
    { name: "Unattempted", value: result.unattempted, color: "#9ca3af" },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      {/* Header */}
      <div className="bg-slate-900 text-white h-16 flex items-center px-6 justify-between shrink-0 shadow-md z-10">
        <Link href="/" className="flex items-center gap-4 hover:opacity-80 transition-opacity cursor-pointer">
          <div className="w-8 h-8 bg-blue-600 rounded flex items-center justify-center font-bold text-lg">
            M
          </div>
          <span className="font-semibold text-lg">Mockzy</span>
        </Link>
        <button
          onClick={() => {
            const returnUrl = exam?.isPYQ && exam?.pyqCategory?.slug
              ? `/pyq/${exam.pyqCategory.slug}`
              : "/practice-exams";
            router.push(returnUrl);
          }}
          className="flex items-center gap-2 text-sm font-medium bg-white/10 hover:bg-white/20 px-4 py-2 rounded-lg transition"
        >
          <ArrowLeft className="w-4 h-4" /> {exam?.isPYQ ? "Back to PYQs" : "Back to Practice"}
        </button>
      </div>

      <div className="flex-1 max-w-6xl w-full mx-auto p-4 md:p-8 overflow-y-auto pb-20">
        {/* Top Score Card */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8 mb-8 flex flex-col md:flex-row gap-8 items-center md:items-stretch overflow-hidden relative">
          
          {/* Left Score Block */}
          <div className="bg-gradient-to-br from-rose-500 to-rose-700 rounded-xl p-8 text-white flex flex-col items-center justify-center md:w-1/3 w-full shrink-0 relative overflow-hidden shadow-inner">
            <div className="w-20 h-20 rounded-2xl border-4 border-white/20 bg-white/10 flex items-center justify-center text-4xl font-black mb-4 backdrop-blur-sm">
              N
            </div>
            <div className="text-5xl font-extrabold tracking-tight mb-1">{result.score}</div>
            <div className="text-rose-100 font-medium uppercase tracking-widest text-sm">Score</div>
          </div>
          
          {/* Right Stats Block */}
          <div className="flex-1 flex flex-col justify-between py-2 w-full">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-gray-900">{exam.examTitle}</h1>
                <span className="px-3 py-1 bg-rose-100 text-rose-700 text-xs font-bold rounded-full">Practice Attempt</span>
              </div>
              <p className="text-gray-500 text-sm">Completed just now</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mt-8">
              <div>
                <div className="flex items-center gap-1.5 text-gray-500 text-[11px] sm:text-xs font-semibold uppercase tracking-wider mb-1">
                  <Clock className="w-4 h-4" /> Time
                </div>
                <div className="text-lg sm:text-xl font-bold text-gray-900">{mins}m {secs}s</div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-gray-500 text-[11px] sm:text-xs font-semibold uppercase tracking-wider mb-1">
                  <Trophy className="w-4 h-4" /> Rank
                </div>
                <div className="text-sm sm:text-xl font-bold text-gray-400">Not Available</div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-gray-500 text-[11px] sm:text-xs font-semibold uppercase tracking-wider mb-1">
                  <TrendingUp className="w-4 h-4" /> Percentile
                </div>
                <div className="text-sm sm:text-xl font-bold text-gray-400">Not Available</div>
              </div>
              <div>
                <div className="flex items-center gap-1.5 text-gray-500 text-[11px] sm:text-xs font-semibold uppercase tracking-wider mb-1">
                  <Target className="w-4 h-4" /> Accuracy
                </div>
                <div className="text-lg sm:text-xl font-bold text-gray-900">{accuracy}%</div>
              </div>
            </div>
          </div>
        </div>

        {/* Question Stats row */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-8">
          <div className="bg-white rounded-xl border border-green-100 p-2 sm:p-4 shadow-sm flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-1 sm:gap-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-green-50 flex items-center justify-center shrink-0">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
            </div>
            <div>
              <div className="text-[10px] sm:text-xs text-gray-500 font-semibold uppercase tracking-wide mb-0.5">Correct</div>
              <div className="text-lg sm:text-2xl font-bold text-green-700 leading-none">{result.correct}</div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-red-100 p-2 sm:p-4 shadow-sm flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-1 sm:gap-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-red-50 flex items-center justify-center shrink-0">
              <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
            </div>
            <div>
              <div className="text-[10px] sm:text-xs text-gray-500 font-semibold uppercase tracking-wide mb-0.5">Wrong</div>
              <div className="text-lg sm:text-2xl font-bold text-red-700 leading-none">{result.incorrect}</div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-2 sm:p-4 shadow-sm flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-1 sm:gap-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-gray-50 flex items-center justify-center shrink-0">
              <MinusCircle className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
            </div>
            <div>
              <div className="text-[10px] sm:text-xs text-gray-500 font-semibold uppercase tracking-wide mb-0.5 truncate max-w-[60px] sm:max-w-none">Unattempted</div>
              <div className="text-lg sm:text-2xl font-bold text-gray-700 leading-none">{result.unattempted}</div>
            </div>
          </div>
        </div>

        {/* Custom Tabs Navigation */}
        <div className="flex border-b border-gray-200 mb-6 overflow-x-auto no-scrollbar">
          {["Summary", "Leaderboard", "Solutions", "Feedback", "Report Error"].map((tab) => {
            const isPremium = tab !== "Summary" && !(tab === "Solutions" && isAuthenticated);
            const isActive = activeTab === tab.toLowerCase().replace(" ", "-");
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab.toLowerCase().replace(" ", "-"))}
                className={`flex items-center gap-2 py-3 px-6 font-semibold text-sm border-b-2 transition-colors whitespace-nowrap ${
                  isActive 
                    ? "border-blue-600 text-blue-600" 
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab} {isPremium && <Lock className="w-3.5 h-3.5" />}
              </button>
            )
          })}
        </div>

        {/* Tab Content */}
        {activeTab === "summary" ? (
          <div className="grid md:grid-cols-2 gap-6">
            {/* Answer Distribution */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-6 text-lg">Answer Distribution</h3>
              <div className="h-[250px] w-full relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => [value, "Questions"]}
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex justify-center gap-6 mt-4">
                {pieData.map(d => (
                  <div key={d.name} className="flex items-center gap-2 text-sm font-medium text-gray-600">
                    <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: d.color }} />
                    {d.name}
                  </div>
                ))}
              </div>
            </div>

            {/* Time Distribution */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
              <h3 className="flex items-center gap-2 font-bold text-gray-900 mb-6 text-lg">
                <Timer className="w-5 h-5 text-gray-400" /> Time Distribution
              </h3>
              
              {/* Stacked Bar */}
              <div className="h-4 w-full bg-gray-100 rounded-full overflow-hidden flex mb-8">
                <div style={{ width: `${correctTimePct}%` }} className="bg-emerald-500 h-full" />
                <div style={{ width: `${incorrectTimePct}%` }} className="bg-red-500 h-full" />
                <div style={{ width: `${unattemptedTimePct}%` }} className="bg-gray-400 h-full" />
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 rounded-lg bg-emerald-50 text-sm">
                  <div className="flex items-center gap-3">
                    <Circle className="w-3 h-3 fill-emerald-500 text-emerald-500" />
                    <span className="font-semibold text-emerald-700">Correct</span>
                  </div>
                  <div className="font-bold text-emerald-700 flex items-center gap-2">
                    {Math.floor(result.correctTime / 60)}m {result.correctTime % 60}s
                    <span className="text-emerald-500/70 font-medium text-xs">({correctTimePct}%)</span>
                  </div>
                </div>
                
                <div className="flex justify-between items-center p-3 rounded-lg bg-red-50 text-sm">
                  <div className="flex items-center gap-3">
                    <Circle className="w-3 h-3 fill-red-500 text-red-500" />
                    <span className="font-semibold text-red-700">Wrong</span>
                  </div>
                  <div className="font-bold text-red-700 flex items-center gap-2">
                    {Math.floor(result.incorrectTime / 60)}m {result.incorrectTime % 60}s
                    <span className="text-red-500/70 font-medium text-xs">({incorrectTimePct}%)</span>
                  </div>
                </div>

                <div className="flex justify-between items-center p-3 rounded-lg bg-gray-50 text-sm">
                  <div className="flex items-center gap-3">
                    <Circle className="w-3 h-3 fill-gray-400 text-gray-400" />
                    <span className="font-semibold text-gray-700">Unattempted</span>
                  </div>
                  <div className="font-bold text-gray-700 flex items-center gap-2">
                    {Math.floor(result.unattemptedTime / 60)}m {result.unattemptedTime % 60}s
                    <span className="text-gray-400 font-medium text-xs">({unattemptedTimePct}%)</span>
                  </div>
                </div>
              </div>
              <div className="text-center text-xs text-gray-400 mt-6 font-medium uppercase tracking-wider">
                Total tracked: {Math.floor(totalTrackedTime / 60)}m {totalTrackedTime % 60}s
              </div>
            </div>

            {/* Performance Breakdown (Locked) */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm relative overflow-hidden flex flex-col min-h-[300px]">
              <div className="blur-sm opacity-40 pointer-events-none select-none flex-1">
                <h3 className="font-bold text-gray-900 mb-6 text-lg">Performance Breakdown</h3>
                <div className="space-y-6">
                  <div>
                    <div className="text-xs font-bold text-gray-500 mb-3 uppercase tracking-wider">SUBJECT-WISE</div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-gray-700">Physics</span>
                      <div className="flex gap-4 text-sm"><span className="text-gray-500">30/30</span><span className="text-green-600 font-bold">25</span><span className="text-gray-400">12m 40s</span></div>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-gray-700">Chemistry</span>
                      <div className="flex gap-4 text-sm"><span className="text-gray-500">30/30</span><span className="text-green-600 font-bold">28</span><span className="text-gray-400">15m 10s</span></div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-700">Mathematics</span>
                      <div className="flex gap-4 text-sm"><span className="text-gray-500">30/30</span><span className="text-green-600 font-bold">20</span><span className="text-gray-400">22m 15s</span></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 backdrop-blur-[2px] text-center p-4">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-3 shadow-sm">
                  <Lock className="w-6 h-6" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">
                  {isAuthenticated ? "Detailed Analysis Unavailable" : "Detailed Analysis Locked"}
                </h4>
                <p className="text-xs text-gray-600 mb-4 max-w-[200px]">
                  {isAuthenticated 
                    ? "Available only for Test Series exams." 
                    : "Sign in to view subject & topic wise performance."}
                </p>
                {!isAuthenticated && (
                  <button onClick={() => router.push("/login")} className="px-5 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg shadow-sm hover:bg-blue-700 transition">Log In</button>
                )}
              </div>
            </div>

            {/* You vs Others (Locked) */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm relative overflow-hidden flex flex-col min-h-[300px]">
              <div className="blur-sm opacity-40 pointer-events-none select-none flex-1">
                <h3 className="flex items-center gap-2 font-bold text-gray-900 mb-6 text-lg">
                  <Trophy className="w-5 h-5 text-gray-400" /> You vs Others
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between text-sm font-bold text-gray-500 border-b pb-2">
                    <span>Metric</span><span>You</span><span className="text-yellow-600">Topper</span><span>Avg</span>
                  </div>
                  <div className="flex justify-between text-sm items-center py-1">
                    <span className="font-medium text-gray-700">Score</span><span className="font-bold text-blue-600">120/300</span><span className="font-bold text-yellow-600">285/300</span><span className="font-bold text-gray-600">85/300</span>
                  </div>
                  <div className="flex justify-between text-sm items-center py-1">
                    <span className="font-medium text-gray-700">Accuracy</span><span className="font-bold text-blue-600">75%</span><span className="font-bold text-yellow-600">98%</span><span className="font-bold text-gray-600">45%</span>
                  </div>
                  <div className="flex justify-between text-sm items-center py-1">
                    <span className="font-medium text-gray-700">Time</span><span className="font-bold text-blue-600">55m</span><span className="font-bold text-yellow-600">170m</span><span className="font-bold text-gray-600">85m</span>
                  </div>
                </div>
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/60 backdrop-blur-[2px] text-center p-4">
                <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-3 shadow-sm">
                  <Lock className="w-6 h-6" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Compare Your Rank</h4>
                <p className="text-xs text-gray-600 mb-4 max-w-[200px]">
                  {isAuthenticated
                    ? "Rank comparisons require thousands of students and are exclusive to Test Series exams."
                    : "Sign in to see where you stand against thousands of students."}
                </p>
                {!isAuthenticated && (
                  <button onClick={() => router.push("/register")} className="px-5 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg shadow-sm hover:bg-blue-700 transition">Create Free Account</button>
                )}
              </div>
            </div>
          </div>
        ) : activeTab === "solutions" && isAuthenticated ? (
          <div className="space-y-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Detailed Solutions</h2>
            </div>
            {exam.questions.map((q, idx) => {
              const isNumerical = q.type === "numerical";
              const isMatch = q.type === "match";
              const userAns = answers[q.id];
              
              let isCorrect = false;
              let correctAnsText = "";
              if (q.type === "mcq" || q.type === "match") {
                const correctOpt = q.options.find((o) => o.isCorrect);
                isCorrect = !!(correctOpt && userAns === correctOpt.key);
                correctAnsText = correctOpt ? `Option ${correctOpt.key}` : "";
              } else if (q.type === "numerical") {
                const numAns = parseFloat(userAns as string);
                isCorrect =
                  q.correctAnswerExact != null
                    ? numAns === q.correctAnswerExact
                    : q.correctAnswerMin != null && q.correctAnswerMax != null
                      ? numAns >= (q.correctAnswerMin as number) && numAns <= (q.correctAnswerMax as number)
                      : false;
                correctAnsText = q.correctAnswerExact != null ? String(q.correctAnswerExact) : `${q.correctAnswerMin ?? ''} - ${q.correctAnswerMax ?? ''}`;
              }

              const isUnattempted = userAns === undefined || userAns === null || userAns === "";

              return (
                <div key={q.id} className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                  <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                    <h3 className="font-bold text-lg text-gray-800">Question {idx + 1}</h3>
                    <div className="flex gap-3 text-sm font-semibold">
                       <span className="px-3 py-1 bg-gray-100 rounded-full text-gray-600">+{q.marks} / -{q.negativeMarks}</span>
                       <span className={`px-3 py-1 rounded-full text-white ${isUnattempted ? 'bg-gray-400' : isCorrect ? 'bg-green-500' : 'bg-red-500'}`}>
                         {isUnattempted ? 'Unattempted' : isCorrect ? 'Correct' : 'Incorrect'}
                       </span>
                    </div>
                  </div>
                  
                  <div className="text-gray-800 mb-6 font-medium leading-relaxed">
                    <SafeHtml html={q.statement} />
                    {q.imageUrl && <img src={q.imageUrl} alt="Question" className="max-w-md h-auto rounded-lg mt-4 border shadow-sm" />}
                  </div>

                  {isMatch && q.matchPairs && <MatchTable matchPairs={q.matchPairs} />}

                  {!isNumerical && (
                    <div className="space-y-3 mb-6">
                      {q.options.map(opt => (
                        <div key={opt.key} className={`p-4 border-2 rounded-lg flex gap-3 items-start ${opt.isCorrect ? 'border-green-500 bg-green-50' : userAns === opt.key ? 'border-red-500 bg-red-50' : 'border-gray-100'}`}>
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 font-bold text-sm ${opt.isCorrect ? 'bg-green-500 text-white' : userAns === opt.key ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-500'}`}>
                            {opt.key}
                          </div>
                          <div className="text-gray-700 font-medium">
                            <SafeHtml html={opt.text} />
                            {opt.imageUrl && <img src={opt.imageUrl} alt="Option" className="max-w-sm h-auto rounded mt-3 border" />}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {isNumerical && (
                    <div className="flex flex-col gap-3 mb-6 max-w-sm">
                       <div className="p-3 border-2 border-green-500 bg-green-50 rounded-lg text-green-800 font-bold">
                         Correct Answer: {correctAnsText}
                       </div>
                       {!isUnattempted && (
                         <div className={`p-3 border-2 rounded-lg font-bold ${isCorrect ? 'border-green-500 bg-green-50 text-green-800' : 'border-red-500 bg-red-50 text-red-800'}`}>
                           Your Answer: {userAns}
                         </div>
                       )}
                    </div>
                  )}

                  <div className="bg-blue-50/60 rounded-xl p-5 border border-blue-100">
                    <h4 className="font-bold text-blue-900 flex items-center gap-2 mb-3 uppercase tracking-wider text-xs">
                      Explanation
                    </h4>
                    <div className="text-gray-700 leading-relaxed font-medium">
                      {q.explanation ? <SafeHtml html={q.explanation} /> : <span className="italic text-gray-500">No explanation provided.</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* Premium Locked State Preview */
          <div className="relative w-full rounded-2xl border border-gray-200 overflow-hidden bg-white min-h-[400px]">
            {/* Blurred Mock Content */}
            <div className="absolute inset-0 p-8 opacity-40 blur-sm pointer-events-none select-none">
              <div className="w-1/3 h-6 bg-gray-300 rounded mb-6"></div>
              <div className="space-y-4">
                <div className="w-full h-12 bg-gray-100 rounded"></div>
                <div className="w-full h-12 bg-gray-100 rounded"></div>
                <div className="w-full h-12 bg-gray-100 rounded"></div>
                <div className="w-3/4 h-12 bg-gray-100 rounded"></div>
              </div>
            </div>

            {/* Glass Overlay CTA */}
            <div className="absolute inset-0 bg-white/50 backdrop-blur-[2px] flex flex-col items-center justify-center p-6 text-center">
              <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-2xl flex items-center justify-center mb-6 shadow-sm">
                <Lock className="w-8 h-8" />
              </div>
              <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
                {isAuthenticated ? "Detailed Insights Unavailable" : "Unlock Detailed Insights"}
              </h2>
              <p className="text-gray-600 max-w-md mb-8 text-lg">
                {isAuthenticated
                  ? "Detailed subject-wise performance and rank comparisons are only available for full Test Series exams, as they require large-scale data."
                  : "Create a free account to access full solutions, subject-wise performance breakdown, and rank against thousands of other students."}
              </p>
              
              {isAuthenticated ? (
                <button
                  onClick={() => router.push("/")}
                  className="px-8 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-600/20 transition-all hover:-translate-y-0.5"
                >
                  Explore Test Series
                </button>
              ) : (
                <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                  <button
                    onClick={() => router.push("/register")}
                    className="px-8 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-600/20 transition-all hover:-translate-y-0.5"
                  >
                    Sign Up Free
                  </button>
                  <button
                    onClick={() => router.push("/login")}
                    className="px-8 py-3.5 bg-white border-2 border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50 font-bold rounded-xl transition-all"
                  >
                    Log In
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Exam Interface ────────────────────────────────────────────────────

export default function GuestExamClient({ slug }: { slug: string }) {
  const router = useRouter();

  const [exam, setExam] = useState<ExamData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);

  const [answers, setAnswers] = useState<
    Record<string, string | number | null>
  >({});
  const [numericalDisplays, setNumericalDisplays] = useState<
    Record<string, string>
  >({});
  const [markedForReview, setMarkedForReview] = useState<
    Record<string, boolean>
  >({});
  const [visitedQuestions, setVisitedQuestions] = useState<Set<string>>(
    new Set(),
  );
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [viewportHeight, setViewportHeight] = useState("100vh");
  const [isMaximizeMode, setIsMaximizeMode] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showMobilePalette, setShowMobilePalette] = useState(false);
  const [mounted, setMounted] = useState(false);

  // Mount and viewport height tracking
  useEffect(() => {
    setMounted(true);
    // Measure actual viewport height for mobile browsers
    const updateHeight = () => {
      setViewportHeight(`${window.innerHeight}px`);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  const answersRef = useRef<Record<string, string | number | null>>({});
  const markedForReviewRef = useRef<Record<string, boolean>>({});
  const examRef = useRef<ExamData | null>(null);
  const submittedRef = useRef(false);

  // ✅ NEW: per-question time tracking (refs — never cause re-renders)
  // timePerQuestion  : accumulated seconds per questionId
  // questionEnteredAt: Date.now() when student last landed on current question (null = not timing)
  const timePerQuestion = useRef<Record<string, number>>({});
  const questionEnteredAt = useRef<number | null>(null);
  const currentQuestionRef = useRef<number>(0); // mirror of currentQuestion state for use in callbacks

  // keep currentQuestionRef in sync
  useEffect(() => {
    currentQuestionRef.current = currentQuestion;
  }, [currentQuestion]);

  // ── time tracking helpers ──────────────────────────────────────────────────

  /** Pause timer on the question the student is LEAVING. */
  const pauseCurrentQuestionTimer = useCallback(() => {
    if (questionEnteredAt.current === null || !examRef.current) return;
    const qId = examRef.current.questions[currentQuestionRef.current]?.id;
    if (!qId) return;
    const elapsed = Math.floor((Date.now() - questionEnteredAt.current) / 1000);
    timePerQuestion.current[qId] =
      (timePerQuestion.current[qId] || 0) + elapsed;
    questionEnteredAt.current = null;
  }, []);

  /** Start/resume timer for the question the student just ARRIVED on. */
  const resumeCurrentQuestionTimer = useCallback(() => {
    questionEnteredAt.current = Date.now();
  }, []);

  /** Snapshot: flush in-flight time for the current question into the map. */
  const getTimeSnapshot = useCallback((): Record<string, number> => {
    if (questionEnteredAt.current !== null && examRef.current) {
      const qId = examRef.current.questions[currentQuestionRef.current]?.id;
      if (qId) {
        const inFlight = Math.floor(
          (Date.now() - questionEnteredAt.current) / 1000,
        );
        return {
          ...timePerQuestion.current,
          [qId]: (timePerQuestion.current[qId] || 0) + inFlight,
        };
      }
    }
    return { ...timePerQuestion.current };
  }, []);

  // ── tab visibility: pause when hidden, resume when visible ────────────────
  useEffect(() => {
    if (!exam) return;
    const handleVisibilityChange = () => {
      if (document.hidden) {
        pauseCurrentQuestionTimer();
      } else {
        resumeCurrentQuestionTimer();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [exam, pauseCurrentQuestionTimer, resumeCurrentQuestionTimer]);

  // ── start timer for Q1 once exam loads ────────────────────────────────────
  useEffect(() => {
    if (exam && !loading) {
      resumeCurrentQuestionTimer();
    }
  }, [exam, loading, resumeCurrentQuestionTimer]);

  // ─────────────────────────────────────────────────────────────────────────

  const setAnswersWithRef = useCallback(
    (
      updater: (
        prev: Record<string, string | number | null>,
      ) => Record<string, string | number | null>,
    ) => {
      setAnswers((prev) => {
        const next = updater(prev);
        answersRef.current = next;
        return next;
      });
    },
    [],
  );

  const setMarkedWithRef = useCallback(
    (updater: (prev: Record<string, boolean>) => Record<string, boolean>) => {
      setMarkedForReview((prev) => {
        const next = updater(prev);
        markedForReviewRef.current = next;
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    examRef.current = exam;
  }, [exam]);

  // ─── Fetch exam data ──────────────────────────────────────────────────────

  useEffect(() => {
    fetchExamData();
  }, [slug]);

  const fetchExamData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/guest-exam/${slug}`);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Failed to load exam");
      }
      const data = await res.json();
      setExam(data.examData);
      examRef.current = data.examData;

      // No saved answers for guest exams
      const remaining = data.examData.duration * 60;
      setTimeRemaining(remaining);
    } catch (error: any) {
      toast.error(error.message || "Failed to load exam");
      router.push("/practice-exams");
    } finally {
      setLoading(false);
    }
  };

  // ─── Countdown timer ──────────────────────────────────────────────────────

  useEffect(() => {
    if (submitted || !exam) return;
    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleSubmit(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [exam, submitted]);

  // ─── Security event listeners ─────────────────────────────────────────────

  useEffect(() => {
    if (!exam) return;

    // Attempt to go fullscreen automatically on first interaction
    const attemptFS = async () => {
      try {
        if (!document.fullscreenElement) {
          await document.documentElement.requestFullscreen();
        }
      } catch (e) {}
    };
    
    const onInteract = () => {
      attemptFS();
      document.removeEventListener('click', onInteract);
    };
    document.addEventListener('click', onInteract);

    const handleFSChange = () =>
      setIsMaximizeMode(!!document.fullscreenElement);
    const blockCtxMenu = (e: MouseEvent) => e.preventDefault();
    const blockCopy = (e: ClipboardEvent) => e.preventDefault();
    document.addEventListener("fullscreenchange", handleFSChange);
    document.addEventListener("contextmenu", blockCtxMenu);
    document.addEventListener("copy", blockCopy);
    document.addEventListener("cut", blockCopy);
    return () => {
      document.removeEventListener('click', onInteract);
      document.removeEventListener("fullscreenchange", handleFSChange);
      document.removeEventListener("contextmenu", blockCtxMenu);
      document.removeEventListener("copy", blockCopy);
      document.removeEventListener("cut", blockCopy);
    };
  }, [exam]);

  // ─── Fullscreen toggle ────────────────────────────────────────────────────

  const toggleFullScreen = async () => {
    if (!document.fullscreenElement) {
      try {
        await document.documentElement.requestFullscreen();
      } catch (e) {}
    } else {
      document.exitFullscreen?.();
    }
  };

  // ─── Answer handlers ──────────────────────────────────────────────────────

  const handleOptionSelect = useCallback(
    (questionId: string, optionKey: string) => {
      setVisitedQuestions((prev) => new Set(prev).add(questionId));
      setAnswersWithRef((prev) => ({
        ...prev,
        [questionId]: prev[questionId] === optionKey ? null : optionKey,
      }));
    },
    [setAnswersWithRef],
  );

  const handleNumericalAnswer = useCallback(
    (questionId: string, display: string, numValue: number | null) => {
      setVisitedQuestions((prev) => new Set(prev).add(questionId));
      setNumericalDisplays((prev) => ({ ...prev, [questionId]: display }));
      setAnswersWithRef((prev) => ({ ...prev, [questionId]: numValue }));
    },
    [setAnswersWithRef],
  );

  const handleClearAnswer = useCallback(() => {
    const qId = exam?.questions[currentQuestion]?.id;
    if (!qId) return;
    setVisitedQuestions((prev) => new Set(prev).add(qId));
    setAnswersWithRef((prev) => ({ ...prev, [qId]: null }));
    setNumericalDisplays((prev) => ({ ...prev, [qId]: "" }));
    setMarkedWithRef((prev) => ({ ...prev, [qId]: false }));
  }, [exam, currentQuestion, setAnswersWithRef, setMarkedWithRef]);

  // ─── Navigation handlers ──────────────────────────────────────────────────

  const handleSaveAndNext = useCallback(() => {
    const qId = exam?.questions[currentQuestion]?.id;
    if (qId) {
      setVisitedQuestions((prev) => new Set(prev).add(qId));
      if (
        answersRef.current[qId] !== null &&
        answersRef.current[qId] !== undefined
      ) {
        setMarkedWithRef((prev) => ({ ...prev, [qId]: false }));
      }
    }

    pauseCurrentQuestionTimer();

    if (currentQuestion < (exam?.totalQuestions || 0) - 1) {
      setCurrentQuestion((prev) => {
        const next = prev + 1;
        currentQuestionRef.current = next;
        resumeCurrentQuestionTimer();
        return next;
      });
    }
  }, [
    exam,
    currentQuestion,
    setMarkedWithRef,
    pauseCurrentQuestionTimer,
    resumeCurrentQuestionTimer,
  ]);

  const handleSaveAndMarkForReview = useCallback(() => {
    const qId = exam?.questions[currentQuestion]?.id;
    if (qId) {
      setVisitedQuestions((prev) => new Set(prev).add(qId));
      setMarkedWithRef((prev) => ({ ...prev, [qId]: true }));
    }
  }, [exam, currentQuestion, setMarkedWithRef]);

  const handleMarkForReviewAndNext = useCallback(() => {
    handleSaveAndMarkForReview();

    pauseCurrentQuestionTimer();

    if (currentQuestion < (exam?.totalQuestions || 0) - 1) {
      setCurrentQuestion((prev) => {
        const next = prev + 1;
        currentQuestionRef.current = next;
        resumeCurrentQuestionTimer();
        return next;
      });
    }
  }, [
    exam,
    currentQuestion,
    handleSaveAndMarkForReview,
    pauseCurrentQuestionTimer,
    resumeCurrentQuestionTimer,
  ]);

  const handlePaletteNavigate = useCallback(
    (index: number) => {
      pauseCurrentQuestionTimer();
      setCurrentQuestion(index);
      currentQuestionRef.current = index;
      resumeCurrentQuestionTimer();
    },
    [pauseCurrentQuestionTimer, resumeCurrentQuestionTimer],
  );

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = useCallback(
    async (autoSubmit = false) => {
      if (!exam) return;
      if (submittedRef.current) return;
      if (!autoSubmit) {
        setShowSubmitConfirm(true);
        return;
      }

      submittedRef.current = true;
      setSubmitting(true);
      setShowSubmitConfirm(false);

      try {
        pauseCurrentQuestionTimer();

        if (document.fullscreenElement)
          document.exitFullscreen().catch(() => {});
        setSubmitted(true);
      } catch (error: any) {
        console.error("Submission error:", error);
        submittedRef.current = false;
        setSubmitting(false);
        toast.error(
          error.message || "Failed to submit exam. Please try again.",
        );
      }
    },
    [exam, pauseCurrentQuestionTimer],
  );

  // ─── BACK button: also needs pause/resume ────────────────────────────────

  const handleBack = useCallback(() => {
    if (currentQuestion === 0) return;
    pauseCurrentQuestionTimer();
    setCurrentQuestion((prev) => {
      const next = prev - 1;
      currentQuestionRef.current = next;
      resumeCurrentQuestionTimer();
      return next;
    });
  }, [currentQuestion, pauseCurrentQuestionTimer, resumeCurrentQuestionTimer]);

  // ─── Derived state ────────────────────────────────────────────────────────

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const questionStatuses = useMemo(() => {
    if (!exam) return [];
    return exam.questions.map((q) => {
      const savedAns = answers[q.id];
      const isNumerical = q.type === "numerical";
      const hasValidAnswer = isNumerical
        ? savedAns !== null && savedAns !== undefined && savedAns !== ""
        : !!savedAns && q.options.some((opt) => opt.key === savedAns);
      const isMarked = !!markedForReview[q.id];
      const isVisited = visitedQuestions.has(q.id);

      if (hasValidAnswer && isMarked) return "ans_marked";
      if (hasValidAnswer) return "answered";
      if (isMarked) return "marked";
      if (isVisited) return "not_answered";
      return "not_visited";
    });
  }, [exam, answers, markedForReview, visitedQuestions]);

  const stats = useMemo(() => {
    const c = {
      notVisited: 0,
      notAnswered: 0,
      answered: 0,
      marked: 0,
      ansMarked: 0,
    };
    questionStatuses.forEach((s) => {
      if (s === "not_visited") c.notVisited++;
      if (s === "not_answered") c.notAnswered++;
      if (s === "answered") c.answered++;
      if (s === "marked") c.marked++;
      if (s === "ans_marked") c.ansMarked++;
    });
    return c;
  }, [questionStatuses]);

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white">
        <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!exam) return null;

  const question = exam.questions[currentQuestion];
  const isNumerical = question.type === "numerical";
  const isMatch = question.type === "match";

  if (submitted) {
    return <GuestResultScreen exam={exam} answers={answers} timeRemaining={timeRemaining} timePerQuestion={getTimeSnapshot()} />;
  }

  return (
    <>
      <div 
        className="fixed inset-0 z-[50] bg-gray-100 flex flex-col w-screen overflow-hidden"
        style={{ height: viewportHeight }}
      >
        {/* ── Header ── */}
        <div className="bg-white border-b shadow-sm shrink-0">
          <div className="px-3 sm:px-6 py-2 sm:py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-4">
                <div className="w-10 h-10 sm:w-16 sm:h-16 border-2 border-gray-300 rounded flex items-center justify-center bg-gray-50 overflow-hidden shrink-0">
                  <User className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
                </div>
                <div>
                  <div className="text-xs sm:text-sm text-gray-600 truncate max-w-[180px] sm:max-w-none">
                    Candidate Name:{" "}
                    <span className="font-semibold text-orange-600 uppercase">
                      Guest Student
                    </span>
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 truncate max-w-[180px] sm:max-w-none">
                    Exam Name:{" "}
                    <span className="font-semibold text-orange-600 uppercase">
                      {exam.examTitle}
                    </span>
                  </div>
                  <div className="text-xs sm:text-sm text-gray-600 flex items-center gap-1.5 sm:gap-3 mt-0.5">
                    <span className="hidden sm:inline">Remaining Time:</span>
                    <span className="font-bold text-sm sm:text-lg px-2 sm:px-3 py-0.5 rounded bg-[#00A9E0] text-white">
                      {formatTime(timeRemaining)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => setShowSubmitConfirm(true)}
                  className="lg:hidden px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-bold shadow-sm"
                >
                  Submit
                </button>
                <div className="hidden lg:flex items-center gap-3">
                  <button
                    onClick={toggleFullScreen}
                    className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium transition"
                  >
                    {isMaximizeMode ? (
                      <Minimize2 className="w-4 h-4" />
                    ) : (
                      <Maximize2 className="w-4 h-4" />
                    )}
                    {isMaximizeMode ? "Exit Fullscreen" : "Fullscreen"}
                  </button>
                  <div className="px-4 py-2 bg-gray-100 rounded text-sm font-medium">
                    English
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Body ── */}
        <div className="flex flex-1 overflow-hidden">
          {/* ── Question Area ── */}
          <div className="flex-1 flex flex-col overflow-hidden relative">
            <div className="px-6 py-4 border-b bg-white flex justify-between items-center">
              <div className="flex items-center gap-3">
                <h2 className="text-lg font-bold text-gray-800">
                  Question No. {currentQuestion + 1}
                </h2>
                {isNumerical && (
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                    Numerical
                  </span>
                )}
                {isMatch && (
                  <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs font-semibold rounded-full">
                    Match
                  </span>
                )}
              </div>
              <div className="text-sm font-medium text-gray-500 flex gap-4">
                <span>Marks: +{question.marks}</span>
                <span>Negative: -{question.negativeMarks}</span>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 bg-white">
              <div key={question.id} className="max-w-5xl mx-auto">
                <div className="mb-8">
                  <div className="text-lg leading-relaxed font-medium text-gray-800">
                    <SafeHtml html={question.statement} />
                  </div>
                  {question.imageUrl && (
                    <img
                      key={question.id}
                      src={question.imageUrl}
                      alt="Question"
                      className="max-w-full h-auto rounded-lg mt-4 border shadow-sm"
                    />
                  )}
                </div>
                {isMatch && question.matchPairs && (
                  <MatchTable matchPairs={question.matchPairs} />
                )}
                {/* MCQ Options */}
                {!isNumerical && (
                  <div className="space-y-4">
                    {question.options.map((option) => (
                      <div
                        key={option.key}
                        onClick={() =>
                          handleOptionSelect(question.id, option.key)
                        }
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          answers[question.id] === option.key
                            ? "border-blue-600 bg-blue-50 shadow-md"
                            : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 mt-0.5 ${
                              answers[question.id] === option.key
                                ? "border-blue-600 bg-blue-600 text-white"
                                : "border-gray-400 text-gray-500"
                            }`}
                          >
                            {answers[question.id] === option.key && (
                              <div className="w-2 h-2 bg-white rounded-full" />
                            )}
                          </div>
                          <div className="text-base text-gray-700 font-medium">
                            <SafeHtml html={option.text} />
                          </div>
                        </div>
                        {option.imageUrl && (
                          <img
                            key={`${question.id}-${option.key}`}
                            src={option.imageUrl}
                            alt={`Option ${option.key}`}
                            className="max-w-full h-auto rounded mt-3 ml-9 border"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {/* Numerical Calculator */}
                {isNumerical && (
                  <NumericalCalculator
                    questionId={question.id}
                    value={numericalDisplays[question.id] || ""}
                    onChange={handleNumericalAnswer}
                  />
                )}
              </div>
            </div>

            {/* ── Action Bar ── */}
            <div className="px-3 sm:px-6 py-2 sm:py-4 border-t bg-gray-50 shrink-0">
              {/* Desktop Action Bar */}
              <div className="hidden sm:flex flex-wrap items-center justify-between gap-3">
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={handleClearAnswer}
                    className="px-6 py-2 bg-white border border-gray-300 text-gray-700 shadow-sm rounded font-semibold hover:bg-gray-50 transition"
                  >
                    CLEAR RESPONSE
                  </button>
                  <button
                    onClick={handleSaveAndMarkForReview}
                    className="px-6 py-2 bg-orange-500 text-white rounded shadow-sm font-semibold hover:bg-orange-600 transition"
                  >
                    SAVE &amp; MARK
                  </button>
                  <button
                    onClick={handleMarkForReviewAndNext}
                    className="px-6 py-2 bg-blue-600 text-white rounded shadow-sm font-semibold hover:bg-blue-700 transition"
                  >
                    MARK &amp; NEXT
                  </button>
                  <button
                    onClick={handleSaveAndNext}
                    className="px-6 py-2 bg-green-600 text-white rounded shadow-sm font-semibold hover:bg-green-700 transition"
                  >
                    SAVE &amp; NEXT
                  </button>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleBack}
                    disabled={currentQuestion === 0}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded font-semibold hover:bg-gray-300 disabled:opacity-50"
                  >
                    &lt;&lt; BACK
                  </button>
                  <button
                    onClick={handleSaveAndNext}
                    disabled={currentQuestion === exam.totalQuestions - 1}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded font-semibold hover:bg-gray-300 disabled:opacity-50"
                  >
                    NEXT &gt;&gt;
                  </button>
                </div>
              </div>

              {/* Mobile Action Bar */}
              <div className="sm:hidden flex flex-col gap-1.5">
                <div className="grid grid-cols-2 gap-1.5">
                  <button
                    onClick={handleClearAnswer}
                    className="py-2 bg-white border border-gray-300 text-gray-700 shadow-sm rounded text-[11px] font-bold hover:bg-gray-50 active:bg-gray-100"
                  >
                    CLEAR
                  </button>
                  <button
                    onClick={handleSaveAndMarkForReview}
                    className="py-2 bg-orange-500 text-white rounded shadow-sm hover:bg-orange-600 active:bg-orange-700 text-[11px] font-bold"
                  >
                    SAVE &amp; MARK
                  </button>
                  <button
                    onClick={handleMarkForReviewAndNext}
                    className="py-2 bg-blue-600 text-white rounded shadow-sm hover:bg-blue-700 active:bg-blue-800 text-[11px] font-bold"
                  >
                    MARK &amp; NEXT
                  </button>
                  <button
                    onClick={handleSaveAndNext}
                    className="py-2 bg-green-600 text-white rounded shadow-sm hover:bg-green-700 active:bg-green-800 text-[11px] font-bold"
                  >
                    SAVE &amp; NEXT
                  </button>
                </div>
                <div className="flex items-center justify-between gap-1.5">
                  <button
                    onClick={handleBack}
                    disabled={currentQuestion === 0}
                    className="px-2 py-2 bg-gray-200 text-gray-700 rounded text-[11px] font-bold disabled:opacity-50 flex-1 hover:bg-gray-300 active:bg-gray-400"
                  >
                    &lt;&lt; BACK
                  </button>
                  <button
                    onClick={() => setShowMobilePalette(true)}
                    className="px-2 py-2 bg-indigo-600 text-white rounded text-[11px] font-bold shadow-sm flex items-center justify-center gap-1.5 flex-[1.2] hover:bg-indigo-700 active:bg-indigo-800"
                  >
                    <LayoutGrid className="w-3.5 h-3.5" />
                    PALETTE
                  </button>
                  <button
                    onClick={handleSaveAndNext}
                    disabled={currentQuestion === exam.totalQuestions - 1}
                    className="px-2 py-2 bg-gray-200 text-gray-700 rounded text-[11px] font-bold disabled:opacity-50 flex-1 hover:bg-gray-300 active:bg-gray-400"
                  >
                    NEXT &gt;&gt;
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* ── Desktop Question Palette Sidebar ── */}
          <div className="hidden lg:flex w-80 bg-white border-l flex-col shrink-0">
            <div className="p-4 overflow-y-auto flex-1">
              <div className="mb-6 bg-blue-50/50 p-3 rounded-lg border border-blue-100">
                <h3 className="font-bold text-gray-700 mb-3 text-sm uppercase tracking-wide">
                  Legend
                </h3>
                <div className="grid grid-cols-2 gap-y-3 gap-x-2 text-xs text-gray-600 font-medium">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gray-50 border border-gray-300 rounded flex items-center justify-center text-gray-500 font-bold shadow-sm">
                      {stats.notVisited}
                    </div>
                    <span>Not Visited</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-red-500 text-white rounded flex items-center justify-center font-bold shadow-sm">
                      {stats.notAnswered}
                    </div>
                    <span>Not Answered</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-500 text-white rounded flex items-center justify-center font-bold shadow-sm">
                      {stats.answered}
                    </div>
                    <span>Answered</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold shadow-sm">
                      {stats.marked}
                    </div>
                    <span>Marked for Review</span>
                  </div>
                  <div className="col-span-2 flex items-center gap-2 mt-1">
                    <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center relative font-bold shadow-sm">
                      <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-0.5 border border-white">
                        <CheckCircle2 className="w-3 h-3 text-white" />
                      </div>
                    </div>
                    <span>Ans &amp; Marked for Review (Will be evaluated)</span>
                  </div>
                </div>
              </div>

              <div className="mb-2 font-bold text-gray-700 text-sm">
                Question Palette:
              </div>
              <div className="grid grid-cols-5 gap-2">
                {exam.questions.map((q, index) => {
                  const status = questionStatuses[index];
                  const isCurrent = index === currentQuestion;
                  let statusClasses = "";
                  let showReviewIndicator = false;
                  switch (status) {
                    case "ans_marked":
                      statusClasses =
                        "bg-purple-600 text-white border-purple-700 rounded-full";
                      showReviewIndicator = true;
                      break;
                    case "answered":
                      statusClasses =
                        "bg-green-500 text-white border-green-600 rounded";
                      break;
                    case "marked":
                      statusClasses =
                        "bg-purple-600 text-white border-purple-700 rounded-full";
                      break;
                    case "not_answered":
                      statusClasses =
                        "bg-red-500 text-white border-red-600 rounded";
                      break;
                    default:
                      statusClasses =
                        "bg-white text-black border-gray-300 rounded hover:bg-gray-50";
                  }
                  return (
                    <button
                      key={q.id}
                      onClick={() => handlePaletteNavigate(index)}
                      className={`w-full aspect-square flex items-center justify-center text-sm font-bold border transition-all shadow-sm relative ${statusClasses} ${isCurrent ? "ring-2 ring-blue-500 ring-offset-2 z-10" : ""}`}
                    >
                      {index + 1}
                      {showReviewIndicator && (
                        <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-0.5 border border-white z-10">
                          <CheckCircle2 className="w-2.5 h-2.5 text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="p-4 border-t bg-gray-50 mt-auto">
              <button
                onClick={() => setShowSubmitConfirm(true)}
                disabled={submitting}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded shadow font-bold hover:bg-blue-700 disabled:opacity-50 transition uppercase tracking-wide"
              >
                {submitting ? "Submitting..." : "Submit Test"}
              </button>
            </div>
          </div>
        </div>

        {/* ── Mobile Question Palette Sheet ── */}
        {mounted && (
          <Sheet open={showMobilePalette} onOpenChange={setShowMobilePalette}>
            <SheetContent
              side="right"
              className="w-[85vw] sm:max-w-md p-0 flex flex-col z-[100] h-[100dvh]"
            >
              <div className="p-4 border-b flex items-center justify-between shrink-0 bg-white">
                <h2 className="font-bold text-gray-800">Question Palette</h2>
              </div>
              <div className="p-4 overflow-y-auto flex-1 bg-white">
                <div className="mb-6 bg-blue-50/50 p-3 rounded-lg border border-blue-100">
                  <h3 className="font-bold text-gray-700 mb-3 text-sm uppercase tracking-wide">
                    Legend
                  </h3>
                  <div className="grid grid-cols-2 gap-y-3 gap-x-2 text-xs text-gray-600 font-medium">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gray-50 border border-gray-300 rounded flex items-center justify-center text-gray-500 font-bold shadow-sm">
                        {stats.notVisited}
                      </div>
                      <span>Not Visited</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-red-500 text-white rounded flex items-center justify-center font-bold shadow-sm">
                        {stats.notAnswered}
                      </div>
                      <span>Not Answered</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-green-500 text-white rounded flex items-center justify-center font-bold shadow-sm">
                        {stats.answered}
                      </div>
                      <span>Answered</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center font-bold shadow-sm">
                        {stats.marked}
                      </div>
                      <span>Marked for Review</span>
                    </div>
                    <div className="col-span-2 flex items-center gap-2 mt-1">
                      <div className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center relative font-bold shadow-sm">
                        <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-0.5 border border-white">
                          <CheckCircle2 className="w-3 h-3 text-white" />
                        </div>
                      </div>
                      <span>Ans &amp; Marked for Review (Will be evaluated)</span>
                    </div>
                  </div>
                </div>
                <div className="mb-2 font-bold text-gray-700 text-sm">
                  Question Palette:
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {exam.questions.map((q, index) => {
                    const status = questionStatuses[index];
                    const isCurrent = index === currentQuestion;
                    let statusClasses = "";
                    let showReviewIndicator = false;
                    switch (status) {
                      case "ans_marked":
                        statusClasses =
                          "bg-purple-600 text-white border-purple-700 rounded-full";
                        showReviewIndicator = true;
                        break;
                      case "answered":
                        statusClasses =
                          "bg-green-500 text-white border-green-600 rounded";
                        break;
                      case "marked":
                        statusClasses =
                          "bg-purple-600 text-white border-purple-700 rounded-full";
                        break;
                      case "not_answered":
                        statusClasses =
                          "bg-red-500 text-white border-red-600 rounded";
                        break;
                      default:
                        statusClasses =
                          "bg-white text-black border-gray-300 rounded hover:bg-gray-50";
                    }
                    return (
                      <button
                        key={q.id}
                        onClick={() => {
                          handlePaletteNavigate(index);
                          setShowMobilePalette(false);
                        }}
                        className={`w-full aspect-square flex items-center justify-center text-sm font-bold border transition-all shadow-sm relative ${statusClasses} ${isCurrent ? "ring-2 ring-blue-500 ring-offset-2 z-10" : ""}`}
                      >
                        {index + 1}
                        {showReviewIndicator && (
                          <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-0.5 border border-white z-10">
                            <CheckCircle2 className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="p-4 border-t bg-gray-50 shrink-0">
                <button
                  onClick={() => setShowSubmitConfirm(true)}
                  disabled={submitting}
                  className="w-full px-4 py-3 bg-blue-600 text-white rounded shadow font-bold hover:bg-blue-700 disabled:opacity-50 transition uppercase tracking-wide"
                >
                  {submitting ? "Submitting..." : "Submit Test"}
                </button>
              </div>
            </SheetContent>
          </Sheet>
        )}

        {/* ── Submit Confirm Dialog ── */}
        {showSubmitConfirm && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-md w-full border border-gray-100">
              <h3 className="text-2xl font-bold mb-2 text-gray-800">
                Submit Exam?
              </h3>
              <p className="text-gray-500 mb-6">
                You are about to submit your exam. Please confirm.
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-6 border border-gray-100">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-2xl font-bold text-green-600">
                      {stats.answered + stats.ansMarked}
                    </div>
                    <div className="text-xs text-gray-500 font-medium uppercase">
                      Answered
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-red-500">
                      {stats.notAnswered}
                    </div>
                    <div className="text-xs text-gray-500 font-medium uppercase">
                      Not Answered
                    </div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-600">
                      {stats.marked}
                    </div>
                    <div className="text-xs text-gray-500 font-medium uppercase">
                      Marked for Review
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowSubmitConfirm(false)}
                  className="flex-1 px-4 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition"
                >
                  Return to Exam
                </button>
                <button
                  onClick={() => {
                    setShowSubmitConfirm(false);
                    handleSubmit(true);
                  }}
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition shadow-lg shadow-green-600/20"
                >
                  Yes, Submit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
